import React, { useEffect, useRef, useState } from 'react'
import { hapticTick } from '../../../../../../_utils/haptics'
import { remix, CHIPS } from '../../data/remix'
import { parseRequest, applyRequest, NOT_UNDERSTOOD } from '../../data/ask'
import { useGatherAsk, withStylist } from '../../data/askData'
import { baileyReady, briefFor, reviewsFor, askBailey, applyEdit } from '../../data/askAI'
import { postTheme } from '../../render/render'
import AutoGrow from '../AutoGrow'
import BaileyMark from '../BaileyMark'
import Preview from '../Preview'
import { CheckIcon, UndoIcon } from '../icons'
import { scrollWithin } from '../magic'

const reducedMotion = () => { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches } catch (e) { return false } }
const pause = (ms) => new Promise(resolve => setTimeout(resolve, ms))

// What to try, for the kind of post that's open
const EXAMPLES = {
  offer: '20% off balayage this week',
  freeSlots: 'Tomorrow at 2 and 4, post it tonight',
  oneSlot: 'Friday at 3pm for a cut and finish',
  team: 'Meet Jess, our new colour specialist',
  newService: 'Headline: The gloss refresh',
  review: 'Share our latest review',
  priceList: 'Use the prices from our menu',
  tips: 'Tip: use a heat protector every time',
  question: 'How often should I trim? Every 6 weeks',
  giftVouchers: 'For Christmas, post it 1 December',
  freshWork: 'Balayage by Jess, post it Friday at 6pm',
  beforeAfter: 'Colour correction by Jess',
}
// Nothing has changed since Bailey's change (a photo's colour being read
// doesn't count), so Undo still undoes it
const unchangedSince = (post, landed) => !!post && !!landed && (post === landed || (
  post.type === landed.type && post.content === landed.content && post.tune === landed.tune && post.photos === landed.photos
  && post.format === landed.format && post.plannedAt === landed.plannedAt && post.caption === landed.caption && post.family === landed.family && post.layout === landed.layout && post.colourway === landed.colourway
))

// Ask Bailey in words or with a tap. Words that say what to post ("20% off
// balayage this week", "post it Friday at 6pm") are done straight away, with
// what changed and what's still needed; words about the look, and the ideas,
// bring back four versions to choose from. Undo brings the old one back.
// memo keeps Bailey's last answer and looks while other tools are open (a
// post made on the Social home starts with what was done, and its looks)
const BaileyPanel = ({ post, kit, trade, format, captionFor, have, memo, onMemo, onApply, onAsk, onUndo, onGo, onShare, onThinking }) => {
  const [prompt, setPrompt] = useState('')
  const [result, setResult] = useState(() => (memo && memo.result) || null)
  const [answer, setAnswer] = useState(() => (memo && memo.answer) || null)
  const [thinking, setThinking] = useState(false)
  const [stage, setStage] = useState('On it')
  const [used, setUsed] = useState(() => (memo && memo.used != null ? memo.used : null))
  // Bailey's thread for this visit to the studio, kept here (the server keeps
  // nothing), so follow-ups read in context
  const history = useRef((memo && Array.isArray(memo.history) && memo.history) || [])
  const seed = useRef(Math.floor(Math.random() * 1000) + 1)
  const timer = useRef(null)
  const results = useRef(null)
  const input = useRef(null)
  const form = useRef(null)
  const alive = useRef(true)
  const gather = useGatherAsk(have)

  // Answers and new versions scroll into view (on a phone they'd land below)
  useEffect(() => {
    if ((!result && !thinking && !answer) || !results.current) return
    scrollWithin(results.current, { block: 'nearest' })
  }, [result, thinking, answer])
  useEffect(() => () => { alive.current = false; clearTimeout(timer.current); if (onThinking) onThinking(false) }, []) // eslint-disable-line react-hooks/exhaustive-deps
  // A post Bailey just made from the Social home shows its looks too, when asked for
  useEffect(() => {
    if (memo && memo.lookFor && !memo.result) {
      seed.current += 1
      setResult({ ...remix(post, { prompt: memo.lookFor.text, kit, trade, seed: seed.current, keep: memo.lookFor.keep }), text: memo.lookFor.text, chip: null, fromAsk: true, keep: memo.lookFor.keep })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (onMemo) onMemo({ answer, result, used, history: history.current }) }, [answer, result, used]) // eslint-disable-line react-hooks/exhaustive-deps

  const busy = (on) => { setThinking(on); if (onThinking) onThinking(on) }
  const chips = CHIPS.filter(c => (c.id !== 'photo' || post.photoColour) && (c.id !== 'brand' || kit.accent))

  // Looks: a chip, or "try four more" (which keeps what was just asked for)
  const runLooks = (text, chip, more = null) => {
    if (thinking) return
    hapticTick()
    seed.current += 1
    busy(true)
    if (!more) setAnswer(null)
    clearTimeout(timer.current)
    // A short beat so the change reads as Bailey's work, not a flicker
    timer.current = setTimeout(() => {
      const keep = (more && more.keep) || []
      const r = remix(post, { prompt: text, chip, kit, trade, seed: seed.current, keep })
      setResult({ ...r, chip, text, fromAsk: !!(more && more.fromAsk), keep })
      setUsed(null)
      busy(false)
    }, reducedMotion() ? 0 : 700)
  }

  // Words: Bailey (the AI) does what they say, with the salon's own facts;
  // when Bailey can't be reached, the rules do what they can
  const ask = async (text) => {
    const asked = String(text || '').trim()
    if (thinking || !asked) return
    hapticTick()
    const req = parseRequest(asked, { now: new Date() })
    if (req.undo) {
      onUndo()
      setPrompt('')
      setResult(null)
      setAnswer({ asked, changes: [], needs: [], notes: ['Put back as it was.'] })
      return
    }
    if (input.current && window.innerWidth <= 767) input.current.blur()
    setStage('On it')
    busy(true)
    setResult(null)
    setAnswer(null)
    const [data, online] = await Promise.all([gather(req, post).catch(() => ({})), baileyReady(), pause(reducedMotion() ? 0 : 450)])
    const ctx = { ...data, now: new Date(), kit, trade: trade || post.trade, captionFor, text: asked }
    let res = null
    let byBailey = false
    let unreachable = false
    if (online) {
      const slow = setTimeout(() => { if (alive.current) setStage('Still on it') }, 7000)
      const r = await askBailey({ brief: briefFor(post, { text: asked, kit, trade: trade || post.trade, facts: data, now: new Date() }), text: asked, reviews: reviewsFor(data), history: history.current })
      clearTimeout(slow)
      if (r.ok) history.current = r.history
      if (r.ok) {
        const edited = applyEdit(post, r.edit, ctx)
        // One stylist's free times (asked for by name) go on as theirs
        res = { ...edited, post: withStylist(edited.post, ctx) }
        byBailey = true
      } else unreachable = true
    }
    if (!res) {
      if (!req.known) {
        if (alive.current) {
          busy(false)
          setAnswer({ asked, changes: [], needs: [], notes: [unreachable ? 'I couldn’t reach Bailey just then, and I didn’t catch that one on my own. Try again in a moment.' : NOT_UNDERSTOOD], lost: true })
        }
        return
      }
      res = applyRequest(post, req, ctx)
    }
    const changed = res.changes.length > 0
    // Done even if another tool was opened meanwhile
    if (changed) onAsk(res.post, res, alive.current ? form.current : null)
    // Looks to choose from, when the rules did the work and look words were used
    let looks = null
    if (!byBailey && req.look && req.look.known) {
      seed.current += 1
      looks = remix(changed ? res.post : post, { prompt: req.lookText, kit, trade, seed: seed.current, keep: res.given })
    }
    let notes = res.notes
    if (!changed && !looks && !res.needs.length && !notes.length) notes = [req.shareNow ? 'Here’s everything you need to share it.' : byBailey ? (res.say || 'Nothing needed changing.') : 'It’s like that already.']
    if (unreachable && changed) notes = [...notes, 'Bailey was busy, so I did the simple parts. Ask again for the rest.']
    const said = { asked, say: changed ? res.say : '', changes: res.changes, needs: res.needs, notes, landed: changed ? res.post : null }
    const shown = looks ? { ...looks, text: req.lookText, chip: null, fromAsk: true, keep: res.given } : null
    if (!alive.current) {
      if (onMemo) onMemo({ answer: said, result: shown, used: null, history: history.current })
    } else {
      setPrompt('')
      setAnswer(said)
      setResult(shown)
      setUsed(null)
      busy(false)
    }
    if (req.shareNow && onShare) onShare()
  }

  const canUndo = !!(answer && answer.landed && unchangedSince(post, answer.landed))

  return (
    <div className="sx-bailey-panel">
      <div className="sx-bailey-intro">
        <BaileyMark size={40} thinking={thinking} />
        <p>Tell me what you want, in your own words, and I’ll do it. Or tap an idea below for four new looks.</p>
      </div>
      <form ref={form} className="sx-ask" onSubmit={(e) => { e.preventDefault(); ask(prompt) }}>
        <AutoGrow
          ref={input}
          singleLine
          className="sx-ask-input"
          value={prompt}
          onChange={setPrompt}
          placeholder={EXAMPLES[post.type] || 'Make it pink and post it Friday at 6pm'}
          aria-label="What should Bailey do?"
          enterkeyhint="send"
          onKeyDown={(e) => { if (e.key === 'Enter') ask(prompt) }}
        />
        <button type="submit" className="sx-ask-go" disabled={thinking || !prompt.trim()} aria-label="Ask Bailey">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 19V5M5 12l7-7 7 7" /></svg>
        </button>
      </form>
      <div className="sx-ask-chips" aria-label="Ideas">
        {chips.map(c => (
          <button key={c.id} type="button" className={`sx-chip${result && result.chip === c.id ? ' is-on' : ''}`} disabled={thinking} onClick={() => runLooks('', c.id)}>{c.label}</button>
        ))}
      </div>

      <div ref={results} className="sx-remix-slot">
        {thinking && (
          <div className="sx-remix" aria-live="polite">
            <p className="sx-remix-said sx-thinking-line">{stage}<span className="sx-dots" aria-hidden><i /><i /><i /></span></p>
          </div>
        )}
        {!thinking && answer && (
          <div className={`sx-done${answer.lost ? ' is-lost' : ''}`} aria-live="polite">
            <p className="sx-done-asked">“{answer.asked}”</p>
            {answer.say && <p className="sx-done-say">{answer.say}</p>}
            {answer.changes.length > 0 && (
              <ul className="sx-done-list">
                {answer.changes.map((c, i) => <li key={`${i}-${c}`} style={{ animationDelay: `${i * 50}ms` }}><CheckIcon size={14} /><span>{c}</span></li>)}
              </ul>
            )}
            {answer.needs.some(n => !n.ask) && (
              <div className="sx-needs">
                <p className="sx-needs-head">{answer.changes.length ? 'Still to add' : 'I need'}</p>
                <ul>
                  {answer.needs.filter(n => !n.ask).map(n => (
                    <li key={n.text}>
                      <span>{n.text}</span>
                      {n.field && onGo && <button type="button" className="sx-link" onClick={() => { hapticTick(); onGo(n.field) }}>Add</button>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {answer.needs.some(n => n.ask) && (
              <div className="sx-needs">
                <p className="sx-needs-head">Bailey asks</p>
                {answer.needs.filter(n => n.ask).map(n => <p key={n.text} className="sx-done-ask">{n.text}</p>)}
                <button type="button" className="sx-link sx-reply" onClick={() => { hapticTick(); if (input.current) { input.current.focus(); scrollWithin(form.current, { block: 'start' }) } }}>Reply</button>
              </div>
            )}
            {answer.notes.map(n => <p key={n} className="sx-done-note">{n}</p>)}
            {canUndo && (
              <div className="sx-done-actions">
                <button type="button" className="sx-btn" onClick={() => { hapticTick(); onUndo(); setAnswer(a => ({ ...a, landed: null, notes: [...a.notes, 'Undone. It’s back as it was.'] })) }}><UndoIcon size={14} />Undo</button>
              </div>
            )}
          </div>
        )}
        {!thinking && result && (
          <div className="sx-remix" aria-live="polite">
            <p className="sx-remix-said">{result.fromAsk ? 'And some looks to go with it. Tap one to use it.' : result.said}</p>
            <div className="sx-choice-grid">
              {result.variants.map((v, i) => (
                <button
                  key={`${seed.current}-${i}`}
                  type="button"
                  className={`sx-choice sx-remix-card${used === i ? ' is-on' : ''}`}
                  aria-pressed={used === i}
                  style={{ animationDelay: `${i * 70}ms` }}
                  onClick={(e) => { setUsed(i); onApply(v.post, v.note, e.currentTarget) }}
                >
                  <Preview post={v.post} theme={postTheme(v.post, { kit })} format={v.post.format || format} />
                  <span className="sx-choice-sub">{v.note}</span>
                </button>
              ))}
            </div>
            <button type="button" className="sx-btn sx-remix-more" onClick={() => runLooks(result.text, result.chip, result.fromAsk ? result : null)}>Try four more</button>
          </div>
        )}
      </div>
    </div>
  )
}

export default BaileyPanel
